import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Row } from "primereact/row";
import { ColumnGroup } from "primereact/columngroup";
import { getSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";

interface ColumnDefinition {
  field: string;
  header: string;
  formatter?: (value: any, options: any, rowData?: any) => string;
  style?: React.CSSProperties;
  frozen?: boolean;
  key?: string;
}
interface InsuredTableProps {
  data: any[];
  columns: ColumnDefinition[];
  hoverColor: string;
  columnGroup?: React.ReactNode;
  scrollable?: boolean;
  scrollHeight?: string;
  className?: string;
  rowClassName?: (data: any, options: any) => string | object | undefined;
}
const InsuredTable: React.FC<InsuredTableProps> = ({
  data,
  columns,
  hoverColor,
  columnGroup,
  scrollable,
  scrollHeight,
  className = null,
  rowClassName,
}) => {
  const globalTheme = getSessionStore("globalTheme") as THEME;
  const isDark = globalTheme === THEME.DARK;

  const headerColumnGroup = columnGroup ? (
    <ColumnGroup>
      {columnGroup}
      <Row
        style={{
          border: `0.5px solid lightgray`,
        }}
        className="yunus"
      >
        {columns?.map((col) => {
          // Strip left/right from col.style — PrimeReact calculates these for frozen columns
          const { left: _l, right: _r, ...safeStyle } = col.style ?? {};
          return (
            <Column
              key={col.field + (col.key ?? "")}
              field={col.field}
              header={col.header}
              {...(col.frozen && { frozen: true })}
              style={{
                ...safeStyle,
                background: isDark ? "#000" : "#EBEBEF",
                ...(col.frozen && { zIndex: 40 }),
              }}
              align="center"
            />
          );
        })}
      </Row>
    </ColumnGroup>
  ) : null;

  const hasFrozenColumns = columns.some((col) => col.frozen);

  return (
    <div className="">
      <DataTable
        value={data}
        scrollable={scrollable || hasFrozenColumns}
        scrollHeight={scrollHeight}
        tableStyle={{ minWidth: "65rem" }}
        className="p-datatable-striped insured-table"
        headerColumnGroup={headerColumnGroup}
        showGridlines
        {...(rowClassName ? { rowClassName } : {})}
      >
        {columns.map((col) => (
          <Column
            key={col.field + (col.key ?? "")}
            field={col.field}
            header={col.header}
            {...(col.frozen && { frozen: true })}
            style={{ ...col.style, ...(col.frozen && { zIndex: 10 }) }}
            align="center"
            body={(rowData, options) => {
              const value = rowData[col.field];
              const formattedValue = col.formatter ? col.formatter(value, options, rowData) : value;
              return <div className="insured-row">{formattedValue}</div>;
            }}
          />
        ))}
      </DataTable>
      <style>
        {`
      .insured-table .p-datatable-tbody > tr,
      .insured-table .p-datatable-tbody > tr.p-row-odd {
        background: transparent !important;
      }
      .insured-table .p-datatable-tbody > tr > td {
        background: ${isDark ? "#3A3A50" : "#EBEBEF"} !important;
        padding: 0.5rem !important;
      }
      .insured-table .p-datatable-tbody > tr.p-row-odd > td {
        background: ${isDark ? "#2A2A3D" : "#FFFFFF"} !important;
      }
      .insured-table .p-datatable-tbody > tr:hover > td {
        color: ${hoverColor};
        background-color: ${hoverColor}12 !important;
      }
      .insured-table .p-datatable-thead {
        z-index: 30 !important;
      }
      .insured-table .p-datatable-thead > tr > th {
        padding: 0.6rem !important;
      }
      .insured-table .p-datatable-thead > tr:nth-child(2) > th:not(.p-sortable-column.p-highlight) {
        background: ${isDark ? "#2A2A3D" : "#F7F7F8"} !important;
        font-weight: 500 !important;
      }
      .insured-table .p-datatable-thead > tr > th.p-frozen-column {
        position: sticky !important;
        background: ${isDark ? "#2A2A3D" : "#EBEBEF"} !important;
        z-index: 40 !important;
      }
      .insured-table .p-datatable-tbody > tr > td.p-frozen-column {
        position: sticky !important;
        background: ${isDark ? "#3A3A50" : "#EBEBEF"} !important;
        z-index: 10 !important;
      }
      .insured-table .p-datatable-tbody > tr.p-row-odd > td.p-frozen-column {
        background: ${isDark ? "#2A2A3D" : "#FFFFFF"} !important;
      }
      .insured-table .p-datatable-tbody > tr:hover > td.p-frozen-column {
        background-color: ${hoverColor} !important;
        color: #ffffff !important;
      }
      ${className}
    `}
      </style>
    </div>
  );
};
export default InsuredTable;
